import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeftRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const rawMigrations = [
  ["Pinecone", "Qdrant"],
  ["Pinecone", "Chroma"],
  ["Pinecone", "Weaviate"],
  ["Pinecone", "Milvus"],
  ["Milvus", "Weaviate"],
  ["Weaviate", "Milvus"],
  ["Qdrant", "Chroma"],
  ["Chroma", "Qdrant"],
  ["Chroma", "MongoDB"],
  ["MongoDB", "Chroma"],
  ["PGVector", "Milvus"],
  ["Milvus", "PGVector"],
  ["Milvus", "Qdrant"],
  ["Weaviate", "Qdrant"],
  ["Weaviate", "Weaviate"],
  ["Qdrant", "Qdrant"],
  ["Chroma", "Chroma"],
  ["Milvus", "Milvus"],
];

const processed = (() => {
  const map = {};

  rawMigrations.forEach(([from, to]) => {
    const direct = `${from}->${to}`;
    const reverse = `${to}->${from}`;

    if (from === to) {
      map[direct] = { from, to, twoWay: true };
      return;
    }

    if (map[reverse]) {
      map[reverse].twoWay = true;
      return;
    }

    map[direct] = { from, to, twoWay: false };
  });

  return Object.values(map);
})();

export default function MigrationFlow() {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const displayedMigrations = showAll ? processed : processed.slice(0, 9);
  const hasMore = processed.length > 9;

  const handleViewMore = () => {
    navigate('/migrations');
  };

  return (
    <section
      id="vector-database-migration-paths"
      className="w-full min-h-screen flex justify-center items-center py-20 px-4 bg-white"
      aria-labelledby="migration-paths-heading"
    >
      <div className="w-full max-w-5xl flex flex-col items-center space-y-10 text-text-primary">

        <motion.h2
          id="migration-paths-heading"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold text-center tracking-tight text-text-primary"
        >
          Vector Database Migration Paths
        </motion.h2>

        <p className="text-center max-w-3xl text-text-secondary">
          Vector Migration supports seamless vector database migration between popular
          platforms like Pinecone, Qdrant, Chroma, Weaviate, Milvus, MongoDB, and PGVector.
          These migration paths allow AI teams to move vector embeddings safely between
          databases with support for one-way and two-way migrations.
        </p>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {displayedMigrations.map((item, index) => (
            <article
              key={index}
              aria-label={`Migrate ${item.from} to ${item.to} vector database`}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.07 }}
                whileHover={{
                  scale: 1.06,
                  y: -5,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                }}
                className="relative flex items-center justify-between rounded-2xl p-6 shadow-md transition-all overflow-hidden bg-bg-soft border border-border"
              >
                {/* Glow */}
                <motion.div
                  className="absolute inset-0 opacity-0 bg-bg-soft"
                  whileHover={{ opacity: 0.6 }}
                  transition={{ duration: 0.4 }}
                />

                {/* From DB */}
                <div className="font-bold text-lg z-10 text-text-primary">
                  {item.from}
                </div>

                {/* Arrow */}
                <motion.div
                  animate={{
                    x: item.twoWay ? [0, 8, 0] : [0, 10, 0],
                    opacity: [1, 0.7, 1],
                  }}
                  transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
                  className="mx-4 z-10 text-text-primary"
                >
                  {item.twoWay ? (
                    <ArrowLeftRight size={32} />
                  ) : (
                    <ArrowRight size={32} />
                  )}
                </motion.div>

                {/* To DB */}
                <div className="font-bold text-lg z-10 text-text-primary">
                  {item.to}
                </div>
              </motion.div>

              <p className="sr-only">
                Migrate vector embeddings from {item.from} to {item.to} using a secure
                vector database migration workflow.
              </p>
            </article>
          ))}
        </div>

        {/* View More Button */}
        {hasMore && !showAll && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleViewMore}
            className="mt-8 px-8 py-3 font-semibold rounded-full shadow-lg transition-all duration-300 flex items-center gap-2 bg-primary text-white hover:bg-primary-soft"
          >
            View All Migration Paths
            <ArrowRight size={20} />
          </motion.button>
        )}
      </div>

      {/* Background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.15 }}
        transition={{ duration: 1.5 }}
        className="fixed -z-10 inset-0 bg-bg-soft"
      />
    </section>
  );
}